// FudiTap guest entry (Step 3): how this tab reached the restaurant.
//
//   /c/<CODE>          existing FudiTap NFC/QR card (6 chars A-Z0-9)      -> { cardCode }
//   ?table=<token>     table QR printed from FudiTap Business (32 hex)    -> { tableToken }
//
// The entry is only a lookup key: restaurant, branch and table are always
// derived on the server. Exactly one of the two is ever sent.
import { normalizeCardCode, normalizeTableToken } from "@/lib/guestMenu";

export const GUEST_ENTRY_KEY = "fuditap_guest_entry";
export const GUEST_CARD_KEY = "fuditap_guest_card"; // pre-Step-3 key, still read

export function entryFrom(value) {
  if (!value || typeof value !== "object") return null;
  if (value.cardCode !== undefined && value.tableToken === undefined) {
    const cardCode = normalizeCardCode(value.cardCode);
    return cardCode ? { cardCode } : null;
  }
  if (value.tableToken !== undefined && value.cardCode === undefined) {
    const tableToken = normalizeTableToken(value.tableToken);
    return tableToken ? { tableToken } : null;
  }
  return null;
}

// A /c/ path or a ?table= parameter always wins and never falls back to the
// previous restaurant; otherwise the entry this tab last loaded is reused.
export function resolveGuestEntry(pathname, search, storage) {
  if (typeof pathname === "string" && pathname.startsWith("/c/")) {
    try {
      return entryFrom({ cardCode: decodeURIComponent(pathname.slice(3).replace(/\/+$/, "")) });
    } catch {
      return null;
    }
  }
  const params = new URLSearchParams(typeof search === "string" ? search : "");
  if (params.has("table")) {
    return params.getAll("table").length === 1 ? entryFrom({ tableToken: params.get("table") }) : null;
  }
  return storedGuestEntry(storage);
}

export function storedGuestEntry(storage) {
  try {
    const raw = storage?.getItem(GUEST_ENTRY_KEY);
    if (raw) return entryFrom(JSON.parse(raw));
    const legacy = storage?.getItem(GUEST_CARD_KEY);
    return legacy ? entryFrom({ cardCode: legacy }) : null;
  } catch {
    return null;
  }
}

export function storeGuestEntry(storage, entry) {
  try {
    storage?.setItem(GUEST_ENTRY_KEY, JSON.stringify(entry));
    if (entry.cardCode) storage?.setItem(GUEST_CARD_KEY, entry.cardCode);
    else storage?.removeItem(GUEST_CARD_KEY);
  } catch { /* in-memory only */ }
}

/** Query string fragment for GET endpoints. */
export const entryQuery = (entry) =>
  entry.cardCode ? `card=${encodeURIComponent(entry.cardCode)}` : `table=${encodeURIComponent(entry.tableToken)}`;

/** Body fields for POST endpoints. */
export const entryBody = (entry) => (entry.cardCode ? { cardCode: entry.cardCode } : { tableToken: entry.tableToken });

/** Device-local storage scope (collision avoidance only, never authority). */
export const entryScope = (entry) => (entry.cardCode ? `card_${entry.cardCode}` : `table_${entry.tableToken.slice(0, 12)}`);
